/**
 * The wire protocol. One definition, imported by both sides, so a type error
 * catches protocol drift before a deploy does.
 *
 * Farkle has no hidden information — every die is face-up on the felt — so
 * unlike most game protocols this one has no redaction story to tell. The single
 * thing a client must never receive is the PRNG state, and `engine.viewFor` is
 * where that is removed. Everything below is envelope.
 */

import type { Difficulty } from './bot.js';
import type { Phase, Rules, View } from './engine.js';

export interface PublicPlayer {
  id: string;
  name: string;
  online: boolean;
  isHost: boolean;
  /** True for a computer seat, and for a human seat currently played by one. */
  isBot: boolean;
}

/** The host's pending setup, visible to the whole lobby so nobody is surprised. */
export interface LobbySettings extends Rules {
  bots: number;
  difficulty: Difficulty;
}

/**
 * What a new table is set to. 10,000 to win and 500 to get on the board is the
 * most commonly played configuration, and a game people already know the shape
 * of is the one they will actually finish.
 */
export const DEFAULT_LOBBY: LobbySettings = {
  target: 10_000,
  threshold: 500,
  finalRound: true,
  piggyback: true,
  bots: 0,
  difficulty: 'steady',
};

/** Everything one socket is allowed to know about its room. */
export interface RoomView {
  code: string;
  players: PublicPlayer[];
  /** Null until the host starts. */
  game: View | null;
  settings: LobbySettings;
  you: {
    id: string;
    isHost: boolean;
    /** Whether this socket may start or restart the game right now. */
    mayStart: boolean;
    /** Whether the game is waiting on THIS seat. Drives every button. */
    yourTurn: boolean;
  };
  /** Set when the host seat moved on its own, so the UI can say so. */
  hostChangedTo: string | null;
  /** Host-only, and only once the timer has elapsed: someone has dropped. */
  hostAction: { kind: 'kickIdle'; names: string[] } | null;
  phase: Phase | 'lobby';
}

/**
 * `toggle` carries `throwId` for the same reason a form carries a nonce.
 *
 * A phone double-taps, React double-renders under StrictMode, and a reconnect
 * resends. Without the stamp, a tap meant for the throw you were looking at
 * lands on the next one and sets aside a die you never chose — and in this game
 * that silently changes what you banked.
 */
export type ClientMsg =
  | { t: 'create'; name: string }
  | { t: 'join'; code: string; name: string; token?: string }
  | { t: 'settings'; settings: Partial<LobbySettings> }
  | { t: 'start' }
  | { t: 'roll'; throwId: number }
  /** Pick up the last player's leftovers and throw them. Carries the same nonce
   *  as `roll`, because it is a throw — just of somebody else's dice. */
  | { t: 'takeCarry'; throwId: number }
  | { t: 'toggle'; throwId: number; index: number }
  | { t: 'clear'; throwId: number }
  | { t: 'bank'; throwId: number }
  | { t: 'again' }
  | { t: 'kick'; playerId: string }
  | { t: 'ping' };

export type ServerMsg =
  | { t: 'joined'; code: string; playerId: string; token: string; view: RoomView }
  | { t: 'view'; view: RoomView }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'pong' };

export type ErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'GAME_IN_PROGRESS'
  | 'NAME_TAKEN'
  | 'NOT_HOST'
  | 'NOT_YOUR_TURN'
  | 'WRONG_PHASE'
  | 'ILLEGAL_MOVE'
  | 'BAD_INPUT'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'SERVER_RESTARTING';

/** What happened, and what to do about it. */
export const ERROR_TEXT: Record<ErrorCode, string> = {
  ROOM_NOT_FOUND: "That code doesn't match a table. Check it and try again.",
  ROOM_FULL: 'That table already has eight players.',
  GAME_IN_PROGRESS: 'That game has already started. Ask them to deal you in next round.',
  NAME_TAKEN: 'Someone at the table is already using that name. Pick another.',
  NOT_HOST: 'Only the host can do that.',
  NOT_YOUR_TURN: "It's not your turn.",
  WRONG_PHASE: 'The turn moved on. Give it a second.',
  ILLEGAL_MOVE: 'You have to set aside at least one scoring die.',
  BAD_INPUT: "That didn't look right. Try again.",
  RATE_LIMITED: 'Slow down a moment.',
  SERVER_ERROR: 'Something went wrong on our end. Try again.',
  SERVER_RESTARTING: 'The game server is restarting.',
};

/**
 * Application close codes. RFC 6455 §7.4.2 reserves 4000–4999 for exactly this.
 * The client's reconnect policy is keyed on them: 1012 means "back in seconds,
 * retry immediately"; 4001/4003/4004 are terminal and must not be retried.
 */
export const CLOSE = {
  /** IANA 1012 Service Restart — sent to every socket on SIGTERM. */
  SERVICE_RESTART: 1012,
  /** IANA 1013 Try Again Later — capacity. */
  TRY_LATER: 1013,
  /** Host removed this player. Terminal. */
  KICKED: 4001,
  /** The same seat rejoined on a newer socket. This one is a corpse. Terminal. */
  SESSION_REPLACED: 4003,
  /** Socket never sent a valid create/join. Terminal. */
  NEVER_JOINED: 4004,
} as const;

// ── limits, shared so the client can pre-validate and the server can enforce ──

export const MAX_PLAYERS = 8;
export const MIN_PLAYERS = 2;
export const NAME_MAX = 14;
export const CODE_LENGTH = 4;
/** No I, O, 0 or 1 — these get read aloud across a table and typed by people
 *  who are three drinks in. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** Bytes. Anything larger is rejected without parsing. */
export const MAX_MESSAGE_BYTES = 2048;
/**
 * 60 messages per 10 seconds. Higher than a card game needs, because setting
 * aside six dice one tap at a time is genuinely six messages, and a hot streak
 * is three of those in a row before anyone banks.
 */
export const RATE_LIMIT_MESSAGES = 60;
export const RATE_LIMIT_WINDOW_MS = 10_000;

/** Client heartbeat: proves the tab's JS runs AND that its frames reach us. */
export const PING_INTERVAL_MS = 5_000;
/** Server protocol ping. RFC 6455 §5.5.3 makes the browser answer in the stack. */
export const SERVER_PING_MS = 10_000;
/**
 * No inbound frame at all — pong or data — for this long and the socket path is
 * dead. A phone that loses its radio sends no FIN, and Linux would not surface
 * the broken path for ~15 minutes.
 */
export const LIVENESS_TIMEOUT_MS = 25_000;
/**
 * No inbound APPLICATION message for this long and the player is offline, even
 * if the socket still answers protocol pings. These are genuinely different
 * signals: a backgrounded tab keeps answering `ping` frames long after it has
 * stopped running timers.
 */
export const APP_LIVENESS_MS = 20_000;
/** Offline this long and the host is offered a way to move the game along. */
export const HOST_ESCAPE_AFTER_MS = 30_000;
/**
 * Offline this long and the host seat moves. Must exceed APP_LIVENESS_MS:
 * `offlineSince` is backdated to last contact, so anything at or below it would
 * migrate in the same tick that marks the host offline.
 */
export const HOST_MIGRATE_AFTER_MS = 45_000;
/** Idle tables are wiped after this. Heartbeats do not count as activity. */
export const ROOM_IDLE_MS = 45 * 60_000;
/** A table nobody ever joined is swept far sooner — it is an enumeration target. */
export const EMPTY_ROOM_IDLE_MS = 5 * 60_000;
/** A joiner who hits GAME_IN_PROGRESS retries on this cadence rather than giving up. */
export const REJOIN_RETRY_MS = 10_000;

/**
 * How long a FARKLE stamp stays up before the turn passes.
 *
 * The server enforces this as a ceiling and moves the table on by itself. It has
 * to: the alternative is a turn that ends only when the farkling player taps
 * something, and the one thing a person reliably does after farkling is put
 * their phone down.
 */
export const FARKLE_BEAT_MS = 2200;

/** How long a bot pauses before each action, so a turn is legible. */
export const BOT_THINK_MS = 620;
/** Setting a die aside is bookkeeping; it should not cost a full beat. */
export const BOT_PICK_MS = 240;

/**
 * Strip control characters, zero-width characters, and bidi overrides, then
 * collapse whitespace. Applied server-side to every name; the client uses it too
 * so what you type is what you see.
 */
export function sanitizeText(input: string): string {
  return (
    input
      // C0 and C1 control characters, including newlines and tabs.
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')
      // Zero-width space / non-joiner / joiner, bidi marks, embeddings and
      // overrides (U+202A-U+202E and U+2066-U+2069 are the Trojan Source
      // characters, CVE-2021-42574 — in a player list they let one name render
      // as another), word joiner, invisible operators, BOM.
      .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * The key a name collides on. Case-folded and NFKC-normalized, so "emory",
 * "EMORY" and a fullwidth spelling of it are one name at an eight-player table.
 */
export function nameKey(name: string): string {
  return sanitizeText(name).normalize('NFKC').toLowerCase();
}

/** Normalize a typed or pasted table code: uppercase, strip anything not in the alphabet. */
export function normalizeCode(input: string): string {
  return input
    .toUpperCase()
    .split('')
    .filter((ch) => CODE_ALPHABET.includes(ch))
    .join('')
    .slice(0, 6);
}
